import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function PublicReferralPage() {
  const { code } = useParams();
  const [rep, setRep] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/referral/${code}`)
      .then(({ data }) => setRep(data))
      .catch(() => setNotFound(true));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/referral-lead", { rep_code: code, pipeline, ...form });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-igc-bg flex items-center justify-center text-igc-cream" data-testid="referral-link-invalid">
        This link isn't valid — check it and try again.
      </div>
    );
  }
  if (!rep) return null;

  return (
    <div className="min-h-screen bg-igc-bg flex items-center justify-center p-6" data-testid="public-referral-page">
      <div className="max-w-md w-full bg-igc-surface border border-igc-gold/15 p-8">
        <p className="text-igc-muted text-xs uppercase tracking-widest mb-2">Iron Gate Consulting</p>
        <h1 className="font-serif text-2xl text-igc-goldText mb-4">Referred by {rep.display_name}</h1>

        {submitted ? (
          <p className="text-igc-cream text-sm" data-testid="referral-submitted-message">
            Thanks — someone from our team will be in touch shortly.
          </p>
        ) : pipeline ? (
          <form onSubmit={submit} className="space-y-3" data-testid="referral-lead-form">
            <input
              type="text" name="website" autoComplete="off" tabIndex={-1}
              value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
              aria-hidden="true" data-testid="referral-honeypot-field"
            />
            <Input data-testid="referral-first-name" placeholder="First name" title="Your first name." required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Input data-testid="referral-last-name" placeholder="Last name" title="Your last name (optional)." value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            <Input data-testid="referral-email" type="email" placeholder="Email" title="An email address so IGC can follow up with you." required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="referral-phone" placeholder="Phone" title="A phone number so IGC can follow up with you (optional)." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Button data-testid="referral-submit-button" type="submit" disabled={submitting} title="Submits your info to the rep who referred you." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-igc-cream text-sm mb-4">What are you interested in?</p>
            <Button data-testid="referral-choose-funding" onClick={() => setPipeline("funding")} title="Choose this if you're a business owner looking for capital." className="w-full bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">I need business funding</Button>
            <Button data-testid="referral-choose-recruiting" onClick={() => setPipeline("recruiting")} title="Choose this if you want to become an IGC sales rep or affiliate yourself." className="w-full bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">I'm interested in joining as a rep</Button>
            <a href={`/trust-portal/signup?ref=${code}`} data-testid="referral-choose-trust" title="Choose this if you want to protect your assets with a living trust." className="block w-full text-center py-2 bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">I need a living trust</a>
          </div>
        )}
      </div>
    </div>
  );
}
